import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { FormlyFillHeightListHostDirective } from '../../../custom-formly/formly-tabs/formly-fill-height-list-host.directive';
import { JournalAttachedEntityType, JournalSearchInput } from '../../../../generated/generated-types';
import { JournalListWidgetComponent } from '../journal-list-widget/journal-list-widget.component';

/**
 * Formly Journal List Widget Component
 * 
 * This formly component displays a filtered list of journals based on attached entity properties.
 * It can be used in forms to show journals related to specific entities.
 * 
 * Usage examples in formly field configuration:
 * 
 * **Example 1: Using form field value as attachedEntityId**
 * ```typescript
 * {
 *   key: 'productId', // Form field value will be used as attachedEntityId
 *   type: 'formly-journal-list-widget',
 *   props: {
 *     attachedEntityType: 'Product'
 *   }
 * }
 * ```
 * 
 * **Example 2: Using explicit attachedEntityId**
 * ```typescript
 * {
 *   type: 'formly-journal-list-widget',
 *   props: {
 *     attachedEntityType: 'Product',
 *     attachedEntityId: 'product-123',
 *     searchInput: {
 *       employeeId: 'employee-456',
 *       take: 10
 *     }
 *   }
 * }
 * ```
 * 
 * Available props:
 * - attachedEntityType: string - The type of entity the journals are attached to
 * - attachedEntityId: string - The ID of the entity the journals are attached to (optional if using form field key)
 * - searchInput: Partial<JournalSearchInput> - Additional search parameters
 */

/**
 * Properties used for this.field.props by this component
 */
type ComponentFieldProps = {
    /** The type of the entity that is attached to the journal (optional) */
    attachedEntityType?: string;
    /** The ID of the entity that is attached to the journal (optional) */
    attachedEntityId?: string;
    /** Additional search input properties (optional) */
    searchInput?: Partial<JournalSearchInput>;
}

type Props = FormlyFieldProps & ComponentFieldProps;

@Component({
    selector: 'app-formly-journal-list-widget',
    templateUrl: './formly-journal-list-widget.component.html',
    styleUrls: ['./formly-journal-list-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    hostDirectives: [FormlyFillHeightListHostDirective],
    host: {
        '[class.phx-base-list--content-height]': 'isContentHeightList',
    },
    imports: [CommonModule, JournalListWidgetComponent]
})
export class FormlyJournalListWidgetComponent extends FieldType<FieldTypeConfig> implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('journalListWidget', { static: false })
    journalListWidget?: JournalListWidgetComponent;

    attachedEntityType?: JournalAttachedEntityType;
    attachedEntityId?: string;
    searchInput: Partial<JournalSearchInput> = {};

    private valueChangesSubscription?: Subscription;

    /** True when the tab opts out of fill-height (detail `.content` scrolls instead). */
    get isContentHeightList(): boolean {
        return this.props?.fillHeight === false;
    }

    /** When `props.fillHeight === false`, list sizes to rows; detail `.content` scrolls. */
    get fillHeightList(): boolean {
        return !this.isContentHeightList;
    }

    ngOnInit(): void {
        const fieldProps = this.field.props as ComponentFieldProps;

        // Extract filter properties from props
        this.attachedEntityType = fieldProps.attachedEntityType as JournalAttachedEntityType;
        this.attachedEntityId = fieldProps.attachedEntityId;

        // If no explicit attachedEntityId is provided, try to use the form field value
        if (!this.attachedEntityId && this.field.key && this.formControl) {
            this.attachedEntityId = this.formControl.value;

            // Watch for form control value changes and update the filter
            this.valueChangesSubscription = this.formControl.valueChanges.subscribe(value => {
                this.attachedEntityId = value;
                this.updateSearchInput();
                this.applyFilter();
            });
        }

        this.updateSearchInput();
    }

    ngAfterViewInit(): void {
        // Apply the filter after view initialization
        this.applyFilter();
    }

    ngOnDestroy(): void {
        // Clean up subscription
        if (this.valueChangesSubscription) {
            this.valueChangesSubscription.unsubscribe();
        }
    }

    private updateSearchInput(): void {
        const fieldProps = this.field.props as ComponentFieldProps;

        // Build search input with filters
        this.searchInput = {
            ...fieldProps.searchInput,
            attachedEntityType: this.attachedEntityType,
            attachedEntityId: this.attachedEntityId
        };
    }

    private applyFilter(): void {
        // Set the custom search input on the child component
        if (this.journalListWidget && Object.keys(this.searchInput).length > 0) {
            this.journalListWidget.setCustomSearchInput(this.searchInput);
        }
    }
}